import { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const contactInfo = [
  {
    icon: MapPin,
    title: "Visit Us",
    content: "85-72 Elizabeth St. Miramichi, NB E1V 1W2",
    href: "https://maps.google.com/?q=85-72+Elizabeth+St+Miramichi+NB+E1V+1W2",
  },
  {
    icon: Phone,
    title: "Call Us",
    content: "506-210-3395",
    href: "tel:506-210-3395",
  },
  {
    icon: Mail,
    title: "Email Us",
    content: "Info@NBRivercity.com",
    href: "mailto:Info@NBRivercity.com",
  },
  {
    icon: Clock,
    title: "Hours",
    content: "Mon-Sat: 8AM - 6PM",
    href: null,
  },
];

const ContactForm = ({ onSubmit }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const success = await onSubmit(formData);
    
    if (success) {
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
      });
    }
    setIsSubmitting(false);
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-16 md:py-24 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left Side - Contact Info */}
          <div>
            <span className="text-[#2563EB] font-medium text-sm uppercase tracking-wider">
              Contact Us
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-2 mb-4">
              Let's Talk About
              <br />
              <span className="gradient-text">Your Cleaning Needs</span>
            </h2>
            <p className="text-slate-600 text-lg mb-8">
              Have questions? We'd love to hear from you. Send us a message
              and we'll respond as soon as possible.
            </p>

            {/* Contact Info Grid */}
            <div className="grid sm:grid-cols-2 gap-6">
              {contactInfo.map((item, index) => {
                const Icon = item.icon;
                const content = (
                  <>
                    <div className="w-12 h-12 bg-[#2563EB]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-[#2563EB]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{item.title}</p>
                      <p className="text-slate-600 text-sm">{item.content}</p>
                    </div>
                  </>
                );
                
                return item.href ? (
                  <a
                    key={index}
                    href={item.href}
                    target={item.href.startsWith('http') ? '_blank' : undefined}
                    rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="flex items-start gap-4 p-4 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer"
                    data-testid={`contact-info-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {content}
                  </a>
                ) : (
                  <div
                    key={index}
                    className="flex items-start gap-4 p-4"
                    data-testid={`contact-info-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {content}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Side - Form */}
          <div>
            <form
              onSubmit={handleSubmit}
              className="bg-slate-50 rounded-3xl p-6 md:p-8"
              data-testid="contact-form"
            >
              <div className="space-y-5">
                {/* Name */}
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">Full Name</Label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    required
                    className="h-12 rounded-xl border-slate-200 bg-white focus:ring-[#2563EB]/20 focus:border-[#2563EB]"
                    data-testid="contact-name-input"
                  />
                </div>

                {/* Email & Phone Row */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Email</Label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      required
                      className="h-12 rounded-xl border-slate-200 bg-white focus:ring-[#2563EB]/20 focus:border-[#2563EB]"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Phone (Optional)</Label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                      className="h-12 rounded-xl border-slate-200 bg-white focus:ring-[#2563EB]/20 focus:border-[#2563EB]"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">Message</Label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your cleaning needs..."
                    required
                    className="min-h-[150px] rounded-xl border-slate-200 bg-white focus:ring-[#2563EB]/20 focus:border-[#2563EB] resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-14 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full text-lg font-semibold btn-glow transition-all disabled:opacity-50"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    "Sending..."
                  ) : (
                    <>
                      Send Message
                      <Send className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
